import React from "react";
import { makeStyles } from "@material-ui/core";
import { Helmet } from "react-helmet";

// Components
import Hero from "../components/Hero.jsx";
import Services from "../components/Services.jsx";
import Vendors from "../components/Vendors.jsx";
import Workflow from "../components/Workflow.jsx";
import Consult from "../../../PageComponents/Consult/Consult";
import GeoSection from "../../../PageComponents/GeoSection/GeoSection.jsx";
import ServiceSchema from "../../../components/StructuredData/ServiceSchema.jsx";
import OpenGraphMeta from "../../../components/SEO/OpenGraphMeta.jsx";

// Images for Hero section
import AIIcon from "../../../../Images/Service/SubPages/ai-integrations.png";
import HeroAI from "../../../../Images/Service/SubPages/HeroImages/AIIntegrations.png";

// Images for AI Integration service cards
import AIStrategyImage from "../../../../Images/Service/SubPages/ServicesImages/AIIntegrations/AIStrategy.webp";
import CustomAIImage from "../../../../Images/Service/SubPages/ServicesImages/AIIntegrations/CustomAI.webp";
import ProcessAutomationImage from "../../../../Images/Service/SubPages/ServicesImages/AIIntegrations/ProcessAutomation.webp";
import ContinuousOptimizationImage from "../../../../Images/Service/SubPages/ServicesImages/AIIntegrations/ContinuousOptimization.webp";

// Workflow images for AI Integrations (sized 450px x 380px)
import WorkflowDiscoveryImage from "../../../../Images/Service/SubPages/ServicesImages/AIIntegrations/Workflow/WorkflowDiscovery.webp";
import WorkflowDevelopmentImage from "../../../../Images/Service/SubPages/ServicesImages/AIIntegrations/Workflow/WorkflowDevelopment.webp";
import WorkflowDeploymentImage from "../../../../Images/Service/SubPages/ServicesImages/AIIntegrations/Workflow/WorkflowDeployment.webp";

const useStyles = makeStyles((theme) => ({}));

/* Accessible, Business-Focused Content */

// Hero Section
const heroTitle = "Put AI to Work for Your Business";
const heroSubTitle =
  "Stop doing repetitive work manually. Cut down on mistakes. Make better decisions faster. AI handles the boring stuff so your team can focus on growing your business and serving customers.";

// Services Section
const serviceTitle = "What AI Can Do for You";
const serviceSubTitle =
  "Save time. Save money. Grow faster. Here's exactly what you get.";

// Service Cards (4 cards)
const aiCardTitles = [
  "Strategy & ROI Analysis",
  "Custom AI Agent Systems",
  "Process Automation Tool",
  "Data & Analytics Agents",
];
const aiCardSubTitles = [
  "Find out where AI will save you the most time and money. See exactly what tasks can be automated—like customer service, processing invoices, qualifying leads, and scheduling. Get a clear plan showing how much time you'll save in the first 90 days.",
  "Get AI assistants that work with the software you already use. They answer customer questions automatically, sort support tickets, create reports, and approve routine requests—no human needed. Works with your CRM, accounting software, and email.",
  "Stop manually entering data, scheduling meetings, sorting emails, and following up with customers. AI does these tasks in seconds, works 24/7, and never makes mistakes. Your team gets 10-20 hours back every week to focus on important work.",
  "AI spots patterns in your sales, inventory, and customer data that humans miss. Know what customers want before they ask. See which products to stock up on. Find new sales opportunities automatically. Make decisions based on facts, not guesses.",
];
const aiCardImages = [
  AIStrategyImage,
  CustomAIImage,
  ProcessAutomationImage,
  ContinuousOptimizationImage,
];

// Vendors Section
const vendorsTitle = "Our Trusted Partners";

// Workflow Section
const workflowTitle = "How It Works";
const workflowSubTitle =
  "Get results in weeks, not months. Here's the simple process.";

// Workflow Cards (3 cards)
const workflowCardItems = [
  {
    title1: "Week 1: Find What to Automate",
    subtitle1:
      "We look at what takes up your team's time. Figure out which tasks AI can handle. Show you exactly how much time and money you'll save. You get a simple plan with clear numbers—no confusing tech talk.",
    image1: WorkflowDiscoveryImage,
    listItem1: "See What's Taking Time",
    listItem2: "Calculate Money Saved",
    listItem3: "Pick the Best Tasks",
    listItem4: "Get Clear Numbers",
    listItem5: "Approve the Plan",
  },
  {
    title2: "Weeks 2-6: Set Up Your AI",
    subtitle2:
      "We build your AI and connect it to your current software. Test everything to make sure it works right. Train your team so they know exactly how to use it. Your daily work doesn't get interrupted.",
    image2: WorkflowDevelopmentImage,
    listItem6: "Build Your AI",
    listItem7: "Connect Your Software",
    listItem8: "Test Everything",
    listItem9: "Train Your Team",
    listItem10: "Start Using It",
  },
  {
    title3: "Ongoing: Watch It Get Better",
    subtitle3:
      "See exactly how much time and money you're saving. Your AI learns and improves as it works. We keep making it better and add new features. You save more money every month.",
    image3: WorkflowDeploymentImage,
    listItem11: "See Time Saved",
    listItem12: "See Money Saved",
    listItem13: "Make It Better",
    listItem14: "Add New Features",
    listItem15: "Save Even More",
  },
];

const AIIntegrations = () => {
  const classes = useStyles();
  return (
    <div>
      <Helmet>
        <title>AI Integration Services | Miami & NYC | Kinetx Co</title>
        <meta 
          name="description" 
          content="Stop doing repetitive work manually. AI handles boring tasks so your team can focus on growing your Miami or NYC business. See results in weeks." 
        />
        <meta name="keywords" content="AI integration, artificial intelligence services, AI automation, business process automation, AI agents, Miami AI services, NYC AI integration, AI consulting, machine learning integration, intelligent automation" />
        <meta name="robots" content="index, follow" />
        <meta name="author" content="Kinetx Co" />
        <link rel="canonical" href="https://www.kinetx.co/virtual-infrastructure/ai-integrations" />
        
        {/* Additional SEO Meta Tags */}
        <meta property="og:type" content="website" />
        <meta property="og:locale" content="en_US" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:site" content="@KinetxCo" />
        
        {/* Geo-targeting */}
        <meta name="geo.region" content="US-FL" />
        <meta name="geo.region" content="US-NY" />
        <meta name="geo.placename" content="Miami" />
        <meta name="geo.placename" content="New York City" />
        
        {/* Business Schema */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Service",
            "serviceType": "AI Integration Services",
            "provider": {
              "@type": "Organization",
              "name": "Kinetx Co",
              "url": "https://www.kinetx.co"
            },
            "areaServed": [
              {
                "@type": "City",
                "name": "Miami"
              },
              {
                "@type": "City",
                "name": "New York City"
              }
            ],
            "description": "Stop doing repetitive work manually. AI handles boring tasks so your team can focus on growing your Miami or NYC business. See results in weeks."
          })}
        </script>
      </Helmet>
      <OpenGraphMeta
        title="AI Integration Services | Miami & NYC | Kinetx Co"
        description="Stop doing repetitive work manually. AI handles boring tasks so your team can focus on growing your Miami or NYC business. See results in weeks."
        url="https://www.kinetx.co/virtual-infrastructure/ai-integrations"
      />
      <ServiceSchema
        serviceName="AI Integration Services"
        serviceDescription="Stop doing repetitive work manually. AI handles boring tasks so your team can focus on growing your Miami or NYC business. See results in weeks."
        serviceUrl="https://www.kinetx.co/virtual-infrastructure/ai-integrations"
      />
      <Hero
        title={heroTitle}
        subtitle={heroSubTitle}
        image={HeroAI}
        serviceTitle="AI Integrations"
        serviceImage={AIIcon}
        heroImageClass="service-image3"
      />
      <Services
        title={serviceTitle}
        subtitle={serviceSubTitle}
        cardTitles={aiCardTitles}
        cardSubTitles={aiCardSubTitles}
        cardImages={aiCardImages}
      />
      <Vendors title={vendorsTitle} />
      <Workflow
        title={workflowTitle}
        subtitle={workflowSubTitle}
        title1={workflowCardItems[0].title1}
        title2={workflowCardItems[1].title2}
        title3={workflowCardItems[2].title3}
        subtitle1={workflowCardItems[0].subtitle1}
        subtitle2={workflowCardItems[1].subtitle2}
        subtitle3={workflowCardItems[2].subtitle3}
        image1={workflowCardItems[0].image1}
        image2={workflowCardItems[1].image2}
        image3={workflowCardItems[2].image3}
        listItem1={workflowCardItems[0].listItem1}
        listItem2={workflowCardItems[0].listItem2}
        listItem3={workflowCardItems[0].listItem3}
        listItem4={workflowCardItems[0].listItem4}
        listItem5={workflowCardItems[0].listItem5}
        listItem6={workflowCardItems[1].listItem6}
        listItem7={workflowCardItems[1].listItem7}
        listItem8={workflowCardItems[1].listItem8}
        listItem9={workflowCardItems[1].listItem9}
        listItem10={workflowCardItems[1].listItem10}
        listItem11={workflowCardItems[2].listItem11}
        listItem12={workflowCardItems[2].listItem12}
        listItem13={workflowCardItems[2].listItem13}
        listItem14={workflowCardItems[2].listItem14}
        listItem15={workflowCardItems[2].listItem15}
      />
      <GeoSection serviceType="AI integration solutions" />
      <Consult color={"virtual"} />
    </div>
  );
};

export default AIIntegrations;

